import { Server } from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { apiClient } from '@/api/client';
import { useConnectionStore } from '@/stores/connection-store';
import { useToastStore } from '@/stores/toast-store';
import type { SearchItem, SearchProvider } from '../types';
import type { ConnectionConfig } from '@/types';

/**
 * Connections list. Picking a result connects (if needed) and opens the
 * connection's Info tab — same "make this thing live and put it in front
 * of me" intent as clicking it in the sidebar.
 */
export const connectionSearchProvider: SearchProvider = {
  id: 'connections',
  kind: 'connection',
  list: (ctx) => {
    const conns = ctx.queryClient?.getQueryData<ConnectionConfig[]>(['connections']) ?? [];
    return conns.map<SearchItem>((c) => ({
      id: `connection:${c.id}`,
      kind: 'connection',
      label: c.name,
      subtitle: `${c.driverType} · ${c.host}:${c.port} · ${c.databaseName}`,
      icon: Server,
      tint: c.colorTag ?? undefined,
      boost: 0.7,
      action: async () => {
        // Connect if not already. Failure toast is driven by the server.
        const status = useConnectionStore.getState().activeConnections[c.id]?.status;
        if (status !== 'connected') {
          try {
            await apiClient.post(`/api/connections/${c.id}/connect`, {});
            useConnectionStore.getState().setStatus(c.id, 'connected');
          } catch (err) {
            useToastStore.getState().addToast(
              'error',
              `Failed to connect: ${err instanceof Error ? err.message : 'unknown'}`,
            );
            return;
          }
        }
        useWorkspaceStore.getState().openTab({
          type: 'info',
          id: `info:${c.id}`,
          connectionId: c.id,
          objectType: 'connection',
          objectName: c.name,
        });
      },
    }));
  },
};
